import React, { useCallback, useEffect, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { classNames, isMobile } from "lib/utils/helpers";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents } from "lib/utils/constants";
import { CaptchaState, CaptchaStatus } from "./CaptchaWrapper";

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

interface TurnstileWrapperProps {
  context: string;
  onTokenChange: (token: CaptchaState) => void;
}

const TurnstileWrapper: React.FC<TurnstileWrapperProps> = ({ context, onTokenChange }) => {
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const [turnstileLoadStartTime, setTurnstileLoadStartTime] = useState<number | null>(null);

  // Start timer on component mount to track turnstile load time
  useEffect(() => {
    setTurnstileLoadStartTime(Date.now());
  }, []);

  const handleTurnstileSuccess = useCallback(
    (token: string) => {
      onTokenChange({ token, status: CaptchaStatus.SUCCESS });

      // Calculate and log turnstile load time
      if (turnstileLoadStartTime) {
        const timeTaken = Date.now() - turnstileLoadStartTime;
        if (timeTaken > 5000) {
          sendAnalyticsEvent({
            eventName: analyticsEvents.FLO_TURNSTILE_LOAD_TIME_EXCEEDED,
            eventType: "flo_action",
            metaData: {
              turnstileData: {
                context,
                captchaProvider: "TURNSTILE",
                timeTaken: timeTaken,
              },
            },
          });
        }
      }
    },
    [context, turnstileLoadStartTime, sendAnalyticsEvent],
  );

  const handleTurnstileError = useCallback(
    (error: any) => {
      onTokenChange({ token: null, status: CaptchaStatus.ERROR });

      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_TURNSTILE_ERROR,
        eventType: "flo_action",
        metaData: {
          context,
          errorCode: error,
        },
      });
    },
    [context, onTokenChange, sendAnalyticsEvent],
  );

  return (
    <div
      className="w-full rounded-xl border border-gray-light overflow-hidden h-fit max-h-14 mt-3"
      style={{
        borderColor: "var(--flo-primary-dark-color)",
        backgroundColor: "var(--flo-primary-dark-color)",
        color: "var(--flo-primary-btn-text-color)",
      }}>
      <Turnstile
        siteKey={turnstileSiteKey}
        onError={handleTurnstileError}
        onSuccess={handleTurnstileSuccess}
        options={{ size: "flexible", theme: "light", feedbackEnabled: false }}
      />
    </div>
  );
};

export default TurnstileWrapper;
